import { useRef, KeyboardEvent } from 'react';
import { CodeBracketIcon } from '@heroicons/react/24/outline';
import type { TemplatePlaceholder } from '../../lib/emailTemplates';

interface EmailTemplateEditorProps {
  subject: string;
  html: string;
  placeholders: TemplatePlaceholder[];
  onSubjectChange: (value: string) => void;
  onHtmlChange: (value: string) => void;
}

export default function EmailTemplateEditor({
  subject,
  html,
  placeholders,
  onSubjectChange,
  onHtmlChange,
}: EmailTemplateEditorProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const insertPlaceholder = (key: string) => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    const tag = `{{${key}}}`;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const newHtml = html.substring(0, start) + tag + html.substring(end);
    onHtmlChange(newHtml);
    requestAnimationFrame(() => {
      textarea.selectionStart = textarea.selectionEnd = start + tag.length;
      textarea.focus();
    });
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Tab') {
      e.preventDefault();
      const textarea = textareaRef.current;
      if (!textarea) return;
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const newHtml = html.substring(0, start) + '  ' + html.substring(end);
      onHtmlChange(newHtml);
      requestAnimationFrame(() => {
        textarea.selectionStart = textarea.selectionEnd = start + 2;
      });
    }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 rounded-t-xl">
        <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1.5">
          Subject Line
        </label>
        <input
          type="text"
          value={subject}
          onChange={(e) => onSubjectChange(e.target.value)}
          className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange text-sm"
          placeholder="Email subject..."
        />
      </div>

      <div className="px-4 py-2 border-b border-gray-200 bg-white">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1.5 text-xs font-medium text-gray-500 mr-1">
            <CodeBracketIcon className="w-3.5 h-3.5" />
            <span>Insert:</span>
          </div>
          {placeholders.map((p) => (
            <button
              key={p.key}
              type="button"
              onClick={() => insertPlaceholder(p.key)}
              className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-golf-green/10 text-golf-green hover:bg-golf-green/20 transition-colors border border-golf-green/20"
              title={`Insert {{${p.key}}} - ${p.label}`}
            >
              {`{{${p.key}}}`}
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 relative">
        <textarea
          ref={textareaRef}
          value={html}
          onChange={(e) => onHtmlChange(e.target.value)}
          onKeyDown={handleKeyDown}
          className="absolute inset-0 w-full h-full resize-none border-0 focus:ring-0 font-mono text-xs leading-relaxed p-4 text-gray-800 bg-white rounded-b-xl"
          spellCheck={false}
          placeholder="Enter your HTML email template here..."
        />
      </div>
    </div>
  );
}
